// INV35-B1-F1 (docs/build-briefs/INV35-B1-F1-delivery-vendor-picker.md; DEC-300; design/mockups/
// inv35-b1-f1-receive-vendor.html): the pure rules behind the optional vendor on the "Receive stock
// (no purchase order)" screen. No DOM here, so every wording and ordering rule is testable on its
// own (test/ui-receive-vendor-model.test.ts). ui/src/receiveVendor.ts draws what this decides.
//
// A vendor is always a PICK from the tenant's own list (an id, never a typed name), it is never
// preselected, and a person can always leave it empty.

import type { ReceiptVendorOptionDto } from "./api.ts";
import { compareNames } from "./nameSort.ts";

export type VendorOption = ReceiptVendorOptionDto;

export const NO_VENDOR_BOX_TEXT = "No vendor chosen";
// UX105: the pinned bar names the state in two words; the consequence sentences are gone.
export const NO_VENDOR_BAR_NOTE = "No vendor";
export const TURNED_OFF_WARNING =
  "This vendor is turned off. You can still record this delivery against it.";
export const TURNED_OFF_ROW_NOTE = "Turned off in Settings. You can still pick it.";
export const TURNED_OFF_CHOSEN_NOTE = "Turned off in Settings";
export const TURNED_OFF_HEADING = "Turned off";
export const LOAD_FAILED_TEXT = "Could not load the vendor list. Check your connection.";

/** How many "Used lately" chips the sheet shows. */
export const USED_LATELY_MAX = 3;

/** The heading a vendor sits under: its first letter, uppercased, or "#" for a name that does not
 * start with a letter. */
export function letterOf(name: string): string {
  const first = name.trim().charAt(0).toLocaleUpperCase();
  return /\p{L}/u.test(first) ? first : "#";
}

function byName(a: VendorOption, b: VendorOption): number {
  return compareNames(a.name, b.name) || a.id.localeCompare(b.id);
}

export interface VendorGroup {
  /** "A", "B", ... , or "Turned off". Empty for the flat search result. */
  heading: string;
  turnedOff: boolean;
  vendors: VendorOption[];
}

export interface VendorSheetView {
  groups: VendorGroup[];
  /** Vendors listed right now (the rows on the sheet). */
  shown: number;
  /** Every vendor of the tenant, turned-off ones included. */
  total: number;
  /** Vendors turned off in Settings (listed only after the footer chip is tapped). */
  turnedOff: number;
  /** The trimmed query the list was filtered by, "" when none. */
  query: string;
}

/** What the sheet lists. Without a query: the active vendors A to Z under letter headings, then
 * (only when `showTurnedOff`) a "Turned off" section. With a query: one flat list of the vendors
 * whose name contains it, ignoring case, active ones first and turned-off ones only when shown. */
export function sheetView(
  vendors: readonly VendorOption[],
  options: { showTurnedOff: boolean; query: string },
): VendorSheetView {
  const query = options.query.trim();
  const needle = query.toLocaleLowerCase();
  const matches = (v: VendorOption): boolean =>
    needle.length === 0 || v.name.toLocaleLowerCase().includes(needle);
  const active = vendors.filter((v) => v.active && matches(v)).sort(byName);
  const off = vendors.filter((v) => !v.active && matches(v)).sort(byName);
  const turnedOff = vendors.filter((v) => !v.active).length;
  const groups: VendorGroup[] = [];
  if (query.length > 0) {
    const flat = options.showTurnedOff ? [...active, ...off] : active;
    if (flat.length > 0) {
      groups.push({ heading: "", turnedOff: false, vendors: flat });
    }
  } else {
    for (const vendor of active) {
      const heading = letterOf(vendor.name);
      const last = groups[groups.length - 1];
      if (last !== undefined && last.heading === heading && !last.turnedOff) {
        last.vendors.push(vendor);
      } else {
        groups.push({ heading, turnedOff: false, vendors: [vendor] });
      }
    }
    if (options.showTurnedOff && off.length > 0) {
      groups.push({ heading: TURNED_OFF_HEADING, turnedOff: true, vendors: off });
    }
  }
  return {
    groups,
    shown: groups.reduce((n, g) => n + g.vendors.length, 0),
    total: vendors.length,
    turnedOff,
    query,
  };
}

function vendorWord(n: number): string {
  return n === 1 ? "vendor" : "vendors";
}

/** The sheet's footer count. */
export function footerText(view: VendorSheetView, showTurnedOff: boolean): string {
  if (view.query.length > 0) {
    return `${view.shown} of ${view.total} ${vendorWord(view.total)} match "${view.query}"`;
  }
  if (showTurnedOff && view.turnedOff > 0) {
    return `${view.total} ${vendorWord(view.total)}, ${view.turnedOff} turned off shown`;
  }
  const active = view.total - view.turnedOff;
  return `${active} ${vendorWord(active)} in this list`;
}

/** The footer chip that reveals or hides the turned-off vendors; null when there are none. */
export function turnedOffChipLabel(view: VendorSheetView, showTurnedOff: boolean): string | null {
  if (view.turnedOff === 0) {
    return null;
  }
  return showTurnedOff ? "Hide turned off vendors" : "Show turned off vendors";
}

/** The "Used lately" chips: the ids the server named, resolved against the list, active only, at
 * most three, in the server's (newest first) order. An id the list does not hold is dropped. */
export function usedLatelyChips(
  ids: readonly string[],
  vendors: readonly VendorOption[],
): VendorOption[] {
  const byId = new Map(vendors.map((v) => [v.id, v]));
  const chips: VendorOption[] = [];
  for (const id of ids) {
    const vendor = byId.get(id);
    if (vendor !== undefined && vendor.active && !chips.some((c) => c.id === id)) {
      chips.push(vendor);
    }
    if (chips.length === USED_LATELY_MAX) break;
  }
  return chips;
}

/** The item's usual vendor as a one-tap chip, or undefined: never when the server named none, the
 * list does not hold it, it is turned off, or it is the vendor already chosen. */
export function usualVendorChip(
  usualVendorId: string | null,
  vendors: readonly VendorOption[],
  chosenId: string | undefined,
): VendorOption | undefined {
  if (usualVendorId === null || usualVendorId === chosenId) {
    return undefined;
  }
  const vendor = vendors.find((v) => v.id === usualVendorId);
  return vendor !== undefined && vendor.active ? vendor : undefined;
}

/** The pinned bar's line beside Record delivery. */
export function barNote(chosen: VendorOption | undefined): string {
  return chosen === undefined ? NO_VENDOR_BAR_NOTE : `Vendor: ${chosen.name}`;
}

/** The vendor named in a "Recorded this visit" row (UX105: the name alone, or "No vendor"). */
export function recordedVendorText(vendorName: string | undefined): string {
  return vendorName === undefined ? "No vendor" : vendorName;
}

const UNIT_WORDS: Readonly<Record<string, readonly [string, string]>> = {
  bottle: ["bottle", "bottles"],
  bottles: ["bottle", "bottles"],
  ounce: ["ounce", "ounces"],
  ounces: ["ounce", "ounces"],
  oz: ["oz", "oz"],
  unit: ["unit", "units"],
  units: ["unit", "units"],
  can: ["can", "cans"],
  cans: ["can", "cans"],
  keg: ["keg", "kegs"],
  kegs: ["keg", "kegs"],
  case: ["case", "cases"],
  cases: ["case", "cases"],
};

/** UX105: what the person entered, worded to agree: ("2", "Bottles") is "2 bottles" and ("1", "Bottles") is
 * "1 bottle". The history row and the toast both use this, so they cannot disagree with each other or with the
 * ledger's resolved ounces (the review saw "2 Bottles" in the toast and "67.628 oz" in the row). */
export function enteredQuantityText(quantity: string, unitLabel: string): string {
  const number = Number(quantity);
  const words = UNIT_WORDS[unitLabel.trim().toLowerCase()];
  const shown = Number.isFinite(number) ? String(Math.round(number * 10000) / 10000) : quantity;
  if (words === undefined) return `${shown} ${unitLabel.trim().toLowerCase()}`;
  return `${shown} ${number === 1 ? words[0] : words[1]}`;
}

/** The green confirmation after Record delivery: what was recorded, and from whom when a vendor is chosen. */
export function recordedToast(
  quantity: string,
  unitLabel: string,
  productName: string,
  vendor: VendorOption | undefined,
): string {
  const base = `Recorded ${enteredQuantityText(quantity, unitLabel)} of ${productName}`;
  return vendor === undefined ? `${base}.` : `${base} from ${vendor.name}.`;
}
